import { DAY_END_MIN, DAY_START_MIN } from "./constants";
import { addDays } from "./streak";
import type { Energy, ParsedTask, Priority } from "./types";

export interface ParseOptions {
  /** Anchor date (YYYY-MM-DD) used to resolve today/tomorrow/weekday/+Nd. */
  today: string;
  /** Known projects, for resolving a `#hint`. Match by id or name prefix. */
  projects?: ReadonlyArray<{ id: string; name: string }>;
}

const WEEKDAY: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

/** Day-of-week (0=Sun) for a YYYY-MM-DD string, computed in UTC (deterministic). */
function dowOf(isoDate: string): number {
  const [y, m, d] = isoDate.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/**
 * Natural-language quick-add parser — ported from the prototype's parseQuick.
 *
 * Grammar (order-independent, all optional):
 *   #project        project by id or name-prefix
 *   p1..p4 / !1..!4 priority (default 2)
 *   ~30m / ~2h      estimate
 *   deep / light    energy (deep->high, light->low)
 *   by YYYY-MM-DD   deadline
 *   YYYY-MM-DD | today | tom[orrow] | mon..sun | +Nd   do-date
 *   at 3pm | 3pm | 14:00 | at 14    start time (only if inside the 09–18 grid)
 *   *               mark as Top-3 (MIT)
 * Everything left over becomes the title.
 */
export function parseQuickAdd(input: string, opts: ParseOptions): ParsedTask {
  let str = ` ${input} `;
  const result: ParsedTask = { title: "", pri: 2 };

  // #project
  const proj = str.match(/#(\S+)/);
  if (proj) {
    const hint = proj[1]!.toLowerCase();
    result.projectHint = proj[1];
    const match = opts.projects?.find(
      (p) => p.id.toLowerCase() === hint || p.name.toLowerCase().startsWith(hint),
    );
    if (match) result.projectId = match.id;
    str = str.replace(proj[0], " ");
  }

  // priority: p1..p4 or !1..!4
  const pri = str.match(/(?:\bp|!)([1-4])\b/i);
  if (pri) {
    result.pri = Number(pri[1]) as Priority;
    str = str.replace(pri[0], " ");
  }

  // estimate: ~30m / ~2h
  const est = str.match(/~(\d+(?:\.\d+)?)(m|h)\b/i);
  if (est) {
    const n = Number(est[1]);
    result.estMin = est[2]!.toLowerCase() === "h" ? Math.round(n * 60) : Math.round(n);
    str = str.replace(est[0], " ");
  }

  // energy
  if (/\bdeep\b/i.test(str)) {
    result.energy = "high" as Energy;
    str = str.replace(/\bdeep\b/i, " ");
  } else if (/\blight\b/i.test(str)) {
    result.energy = "low" as Energy;
    str = str.replace(/\blight\b/i, " ");
  }

  // deadline: by YYYY-MM-DD
  const ddl = str.match(/\bby\s+(\d{4}-\d{2}-\d{2})\b/i);
  if (ddl) {
    result.deadline = ddl[1];
    str = str.replace(ddl[0], " ");
  }

  // do-date
  const isoDate = str.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (isoDate) {
    result.doDate = isoDate[1];
    str = str.replace(isoDate[0], " ");
  } else if (/\btoday\b/i.test(str)) {
    result.doDate = opts.today;
    str = str.replace(/\btoday\b/i, " ");
  } else if (/\btom(orrow)?\b/i.test(str)) {
    result.doDate = addDays(opts.today, 1);
    str = str.replace(/\btom(orrow)?\b/i, " ");
  } else {
    const wd = str.match(/\b(sun|mon|tue|wed|thu|fri|sat)[a-z]*\b/i);
    if (wd) {
      const target = WEEKDAY[wd[1]!.toLowerCase()]!;
      const delta = ((target - dowOf(opts.today) + 7) % 7) || 7;
      result.doDate = addDays(opts.today, delta);
      str = str.replace(wd[0], " ");
    } else {
      const rel = str.match(/\+(\d+)d\b/i);
      if (rel) {
        result.doDate = addDays(opts.today, Number(rel[1]));
        str = str.replace(rel[0], " ");
      }
    }
  }

  // time: at 3pm / 3pm / 14:00 / at 14  (only when it reads as a time)
  const time = str.match(/\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s?(am|pm)?\b/i);
  if (time && (time[3] || time[2] || /\bat\s/i.test(str))) {
    let h = Number(time[1]);
    const min = time[2] ? Number(time[2]) : 0;
    if (time[3]) {
      const mer = time[3].toLowerCase();
      if (mer === "pm" && h < 12) h += 12;
      if (mer === "am" && h === 12) h = 0;
    }
    const fromMin = h * 60 + min;
    if (fromMin >= DAY_START_MIN && fromMin < DAY_END_MIN) {
      result.fromHour = fromMin;
      if (!result.doDate) result.doDate = opts.today;
      str = str.replace(time[0], " ");
    }
  }

  // MIT flag: a standalone *
  if (/(^|\s)\*(\s|$)/.test(str)) {
    result.mit = true;
    str = str.replace(/(^|\s)\*(\s|$)/, " ");
  }

  result.title = str.replace(/\s+/g, " ").trim();
  return result;
}
